import { dataGet } from "./object.utils"

export const orderArrayOfObjects = (
  array,
  { optionValue, sortType = "asc" }
) => {
  const arrayToSort = [...array]
  return arrayToSort.sort((a, b) => {
    let x = null
    let y = null
    if (optionValue) {
      const typeValue = typeof a[optionValue]
      if (typeValue === "string") {
        x = a[optionValue].trim().toLowerCase()
        y = b[optionValue].trim().toLowerCase()
      } else {
        x = Number(a[optionValue])
        y = Number(b[optionValue])
      }
    } else {
      const typeValue = typeof a
      if (typeValue === "string") {
        x = a.trim().toLowerCase()
        y = b.trim().toLowerCase()
      } else {
        x = Number(a)
        y = Number(b)
      }
    }
    if (x === y) {
      return 0
    }
    const SORT_TYPES = {
      desc: {
        lesserValue: -1,
        higherValue: 1,
      },
      asc: {
        lesserValue: 1,
        higherValue: -1,
      },
    }
    const DEFAULT_SORT_TYPE = "desc"
    if (x < y) {
      return SORT_TYPES[sortType || DEFAULT_SORT_TYPE].lesserValue
    }
    return SORT_TYPES[sortType || DEFAULT_SORT_TYPE].higherValue
  })
}

export const getUniqueValuesFromArrayOfObjects = (
  array = [],
  property = "id"
) => {
  const dataMaped = array.map((el) => el[property])
  const SET = new Set(dataMaped)
  return [...SET]
}

export const indexArrayOfObjectsByProperty = (array = [], property = "id") => {
  const resultIndexed = {}
  array.forEach((element) => {
    const propertyValue = element[property]
    if (resultIndexed[propertyValue]) {
      resultIndexed[propertyValue].push(element)
    } else {
      resultIndexed[propertyValue] = [element]
    }
  })
  return resultIndexed
}

export function arraysContainSameStrings(arr1, arr2) {
  if (arr1.length !== arr2.length) return false

  const sortedArr1 = arr1.slice().sort()
  const sortedArr2 = arr2.slice().sort()

  for (let i = 0; i < sortedArr1.length; i++) {
    if (sortedArr1[i] !== sortedArr2[i]) return false
  }

  return true
}

/**
 * @param {number[]} arr1
 * @param {number[]} arr2
 */
export function arraysContainSameNumbers(arr1, arr2) {
  if (arr1.length !== arr2.length) return false
  const sortedArr1 = arr1.slice().sort((a, b) => a - b)
  const sortedArr2 = arr2.slice().sort((a, b) => a - b)
  return sortedArr1.every((value, index) => value === sortedArr2[index])
}

export const generateIntermediateNumbersOfRange = (start, end, separation) => {
  const result = []
  for (let i = start; i <= end; i += separation) {
    result.push(i)
  }
  return result
}

/**
 * @template T
 * @typedef {((item: T) => string | string[]) | DotNotationPath<T> | Array<DotNotationPath<T>>} ObjectMultiplePropertyAccessor
 */

/**
 * @template T
 * @typedef {((item: T) => string) | DotNotationPath<T>} ObjectPropertyAccessor
 */

/**
 * @template T
 *
 * @param {T[]} arr
 * @param {string} query
 * @param {ObjectMultiplePropertyAccessor<T>} accessor
 * @returns {T[]}
 */
export function search(arr, query, accessor, caseSensitive = false) {
  if (!query) return arr

  const transform = /** @param {string} str */ (str) =>
    caseSensitive ? str : str.toLowerCase()

  const access = /** @param {T} item */ (item) =>
    typeof accessor === "function" ? accessor(item)
    : Array.isArray(accessor) ?
      accessor.map((_accessor) => dataGet(item, _accessor, ""))
    : dataGet(item, accessor, "")

  return arr.filter((item) => {
    let comparables = access(item)

    if (!Array.isArray(comparables)) comparables = [comparables]

    return comparables.some((comparable) =>
      transform(comparable).includes(transform(query))
    )
  })
}

/**
 * @template T
 * @template {keyof T} K
 *
 * @param {T[]} arr
 * @param {ObjectPropertyAccessor<T>} accessor
 * @returns {T[]}
 */
export function sort(arr, accessor, direction = "asc") {
  if (!arr.length) return arr

  const access = /** @param {T} item */ (item) =>
    typeof accessor === "function" ?
      accessor(item)
    : dataGet(item, accessor, "")

  return arr.sort((a, b) => {
    const aVal = access(a)
    const bVal = access(b)

    if (aVal < bVal) return direction === "asc" ? -1 : 1
    if (aVal > bVal) return direction === "asc" ? 1 : -1
    return 0
  })
}

/**
 * @template T
 * @template {keyof T} K
 *
 * @param {T[]} arr
 * @param {ObjectPropertyAccessor<T>} accessor
 */
export function groupBy(arr, accessor) {
  const access = /** @param {T} item */ (item) =>
    typeof accessor === "function" ?
      accessor(item)
    : dataGet(item, accessor, "")

  return arr.reduce((acc, item) => {
    const key = access(item)
    if (!acc[key]) acc[key] = []
    acc[key].push(item)
    return acc
  }, /** @type {Record<string, T[]>} */ ({}))
}

/**
 * @template T
 * @template {keyof T} K
 *
 * @param {T[]} arr
 * @param {ObjectPropertyAccessor<T>} accessor
 */
export function arrayJoin(
  arr,
  accessor,
  separator = ", ",
  lastSeparator = " y "
) {
  const access = /** @param {T} item */ (item) =>
    typeof accessor === "function" ?
      accessor(item)
    : dataGet(item, accessor, "")

  if (!arr.length) return ""

  if (arr.length === 1) return access(arr[0])

  const last = arr.slice(-1)

  return arr
    .slice(0, -1)
    .map(access)
    .join(separator)
    .concat(lastSeparator)
    .concat(access(last[0]))
}

/**
 * @template T
 *
 * @param {T[]} arr
 * @param {ObjectPropertyAccessor<T>} accessor
 * @returns
 */
export function keyBy(arr, accessor) {
  const access = /** @param {T} item */ (item) =>
    typeof accessor === "function" ?
      accessor(item)
    : dataGet(item, accessor, "")

  return arr.reduce((acc, item) => {
    acc[access(item)] = item
    return acc
  }, /** @type {Record<string, T>} */ ({}))
}

/**
 * @template T
 *
 * @param {T[]} arr
 */
export function arrRandom(arr) {
  if (!arr) arr = []

  const randomIndex = Math.floor(Math.random() * arr.length)

  return arr[randomIndex]
}
